import { Box } from '@mui/material';
import React from 'react';

import { createTokens } from '../../../styles/mui/base/create-tokens';
import { ensureListing } from '../../../util/data';
import useAppSelector from '../../../util/hooks/useAppSelector';
import IconMapMarker from '../../Icons/IconMapMarker/IconMapMarker';

const tokens = createTokens();

// `.root` declarations. Scoped to the component's own base class, so they
// apply ONLY when no rootClassName override is passed
// (Sharetribe classNames(rootClassName || css.root, ...)).
const rootSx = {
  position: 'relative',
  width: 'auto',
  height: 'auto',
  border: 0,
  padding: 0,
};

// The `.root:hover .marker` rule. In the original this descendant selector
// (specificity 0,2,1) overrides .markerActive / .markerBookmarked (0,1,0) on
// hover, so it must be emitted LAST to win the cascade. Also gated by the
// absence of a rootClassName override.
const rootHoverSx = {
  '&:hover > svg': {
    cursor: 'pointer',
    // Same as active
    color: tokens.marketplaceColor,
  },
};

// `.marker` declarations. Applied to the IconMapMarker svg regardless of the
// rootClassName override, matching the original markerClasses on the icon.
const markerSx = {
  '& > svg': {
    // Since caret is absolutely positioned, label must have relative to be
    // included to the same rendering layer
    position: 'relative',
    backgroundColor: 'transparent',
    color: tokens.marketplaceColorDark,
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
    // Only `color` actually animates between default / hover / active /
    // bookmarked states — the catch-all `all` transition that the shared
    // token expands to keeps the browser tracking transition state during
    // every Mapbox marker-transform write on pan.
    transition: 'color 0.1s ease-in-out',
    // Overwrite dimensions from font styles
    '@media (min-width:900px)': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
};

const markerActiveSx = {
  // Same as hover
  '& > svg': { color: tokens.marketplaceColor },
};

const markerBookmarkedSx = {
  '& > svg': { color: tokens.marketplaceColorLight },
};

const SearchMapMarkerLabel = ({
  className = null,
  rootClassName = null,
  listing,
  onListingClicked,
  isActive,
}) => {
  const currentListing = ensureListing(listing);

  const currentBookmarks = useAppSelector(state => state.bookmarks.currentBookmarks);
  const isBookmarked = currentBookmarks.some(bookmark => bookmark === currentListing.id.uuid);

  const hasRootOverride = rootClassName != null;

  const sx = {
    // `.root` button style applies only when no rootClassName override.
    ...(hasRootOverride ? {} : rootSx),
    // `.marker` styles always apply to the icon (was markerClasses on the svg).
    ...markerSx,
    // markerActive/markerBookmarked were applied on the marker; active wins
    // over bookmarked because it was listed last in the classNames call.
    ...(isBookmarked ? markerBookmarkedSx : {}),
    ...(isActive ? markerActiveSx : {}),
    // Hover rule must come last so its higher original specificity is honoured.
    ...(hasRootOverride ? {} : rootHoverSx),
  };

  return (
    <Box
      className={className}
      component="button"
      onClick={() => onListingClicked(currentListing)}
      sx={sx}
    >
      <IconMapMarker />
    </Box>
  );
};

export default SearchMapMarkerLabel;
